import { createSelector } from 'reselect';
import { camelCaseToFriendlyText } from 'utils/helpers';

export const checkIfRunning = state =>
  // eslint-disable-next-line no-nested-ternary
  state.order.create.machineIsRunning === 'yes'
    ? true
    : state.order.create.machineIsRunning === 'no'
      ? false
      : null;

export const getFilteredOrderTypes = createSelector(
  [checkIfRunning, state => state.order.create.types || []],
  (isRunning, orderTypes) =>
    orderTypes
      ?.filter(ot => isRunning === null ? true : ot.running === isRunning)
      ?.map(ot => ({
        id: ot.type,
        text: ot.description,
        default: ot.default,
      })),
);

export const getFilteredIndicatorTypes = createSelector(
  [checkIfRunning, state => state.order.create.indicatorTypes || []],
  (isRunning, indicatorTypes) => {
    const filteredIndicatorTypes = indicatorTypes?.filter(ot =>
      isRunning === null ? true : ot.running === isRunning,
    );

    // Removed this code due to the comment in bug 10621
    // (https://acop-dlwr.visualstudio.com/ACOP.CTSIntheCloud/_workitems/edit/10621/)
    // If machine is not running we check for type 'GL' or 'PG'.
    // If one of these types had addIfBreakdownDefault === true
    // and default === true we keep that one in the list.
    // Otherwise we delete it.
    // if (isRunning === false && filteredIndicatorTypes.length) {
    // const extraBreakdownType = filteredIndicatorTypes
    //   ?.filter(
    //     ot =>
    //       ot.indicator.id === indicatorsConfig.productWarrantyPC ||
    //       ot.indicator.id === indicatorsConfig.productWarrantyCC,
    //   )
    //   ?.find(bf => bf.addIfBreakdownDefault && bf.default);
    // const breakdownIndicatorTypes = filteredIndicatorTypes?.filter(
    //   ot =>
    //     ot.indicator.id !== indicatorsConfig.productWarrantyPC &&
    //     ot.indicator.id !== indicatorsConfig.productWarrantyCC,
    // );
    // if (extraBreakdownType) {
    //   breakdownIndicatorTypes.push(extraBreakdownType);
    // }
    //   return breakdownIndicatorTypes
    //     ?.sort((a, b) => b.indicator.importance - a.indicator.importance)
    //     ?.map(ot => ({
    //       id: ot.indicator.id,
    //       text: ot.indicator.description,
    //       hidden: ot.indicator.hidden,
    //       default: ot.default,
    //       inputs: ot.inputs,
    //       splitInvoiceIndicator: breakdownIndicatorTypes?.find(i =>
    //         i.indicator.validationType === ot.indicator.validationType &&
    //         i.indicator.splitInvoice &&
    //         i.running === ot.running,
    //       ),
    //     }))
    //     ?.filter(ot => !ot.hidden);
    // }

    return filteredIndicatorTypes
      ?.sort((a, b) => b.indicator.importance - a.indicator.importance)
      ?.map(ot => ({
        id: ot.indicator.id,
        text: ot.indicator.description,
        hidden: ot.indicator.hidden,
        default: ot.default,
        inputs: ot.inputs,
        splitInvoiceIndicator: filteredIndicatorTypes?.find(i =>
          i.indicator.validationType === ot.indicator.validationType &&
          i.indicator.splitInvoice &&
          i.running === ot.running,
        ),
      }))
      ?.filter(ot => !ot.hidden);
  },
);

export const getDescriptions = createSelector(
  [
    state => state.order.create.types || [],
    state => state.form.newOrderForm && state.form.newOrderForm.values,
  ],
  (orderTypes, values) => {
    if (values && values.orderType) {
      if (orderTypes?.find(ot => ot.type === values.orderType)) {
        return orderTypes
          ?.find(ot => ot.type === values.orderType)
          .descriptions?.map(d => ({
            id: d.description,
            text: camelCaseToFriendlyText(d.description),
            default: d.default,
            input: d.input,
          }));
      }
      return [];
    }
    return [];
  },
);

export const getDescriptionField = createSelector(
  [getDescriptions, state => state.form.newOrderForm && state.form.newOrderForm.values],
  (descriptions, values) => {
    const description = values && descriptions?.find(v => v.id === values.description);
    return description;
  },
);

export const getSplitInvoiceIndicator = createSelector(
  [getFilteredIndicatorTypes, state => state.form.newOrderForm && state.form.newOrderForm.values],
  (types, values) => types?.find(t => t.id === values?.indicatorType)?.splitInvoiceIndicator,
);

export const getOrderTypeField = createSelector(
  [getFilteredOrderTypes, state => state.form.newOrderForm && state.form.newOrderForm.values],
  (orderTypes, values) => {
    const orderType = values && orderTypes?.find(v => v.id === values.orderType);
    return orderType;
  },
);

export const getIndicatorTypes = createSelector(
  [state => state.order.create.indicatorTypes || []],
  indicators => indicators
    ?.map(ot => ({
      id: ot.indicator.id,
      text: ot.indicator.description,
      hidden: ot.indicator.hidden,
      default: ot.default,
      running: ot.running,
      inputs: ot.inputs,
      splitInvoiceIndicator: indicators?.find(i =>
        i.indicator.validationType === ot.indicator.validationType &&
        i.indicator.splitInvoice &&
        i.running === ot.running,
      ),
    }))
    ?.filter(ot => !ot.hidden),
);

export const getIndicatorInputs = createSelector(
  [getIndicatorTypes, state => state.form.newOrderForm && state.form.newOrderForm.values],
  (indicatorTypes, values) => {
    if (values && values.indicatorType) {
      const selectedIndicator = indicatorTypes?.find(it => it.id === values.indicatorType);
      if (selectedIndicator) {
        return selectedIndicator.inputs;
      }
      return [];
    }
    return [];
  },
);

export const getIndicatorDescription = createSelector(
  [state => state.form.newOrderForm && state.form.newOrderForm.values, getIndicatorInputs],
  (values, inputs) => {
    // Check if all other fields are filled in.
    if (values && !Object.values(values)?.find(v => !v)) {
      if (inputs && inputs.length) {
        return inputs
          ?.map((input) => {
            if (Object.keys(values)?.find(v => v === input.name) && values[input.name]) {
              return true;
            }
            return false;
          })
          ?.find(v => v);
      }
      return true;
    }
    return false;
  },
);

export const getCostPaidBys = createSelector(
  [
    state => state.order.create.types || [],
    getDescriptions,
    state => state.form.newOrderForm && state.form.newOrderForm.values,
  ],
  (orderTypes, descriptions, values) => {
    if (
      values &&
      (values.description ||
        (descriptions.length === 0 && orderTypes.length > 0 && values.orderType))
    ) {
      if (orderTypes?.find(ot => ot.type === values.orderType)) {
        return orderTypes
          ?.find(ot => ot.type === values.orderType)
          .paidBys?.map(d => ({
            id: d.type,
            text: camelCaseToFriendlyText(d.type),
            default: d.default,
            indicators: d.indicators,
            paidByValues: d.values,
            paidByValueKey: d.paidByValueLabelKey,
            wizardInputs: d.wizardInputs,
          }));
      }
      return [];
    }
    return [];
  },
);

export const getIndicators = createSelector(
  [getCostPaidBys, state => state.form.newOrderForm && state.form.newOrderForm.values],
  (costPaidBys, values) => {
    if (values && values.paidBy && costPaidBys.length > 0) {
      if (costPaidBys?.find(cpb => cpb.id === values.paidBy)) {
        const { indicators } = costPaidBys
          ?.find(cpb => cpb.id === values.paidBy);
        return indicators
          ?.map(d => ({
            id: d.id,
            text: d.description,
            hidden: d.hidden,
            splitInvoiceIndicator: indicators?.find(i =>
              i.validationType === d.validationType && i.splitInvoice,
            ),
          }))
          ?.filter(d => !d.hidden);
      }
      return [];
    }
    return [];
  },
);

export const getBusinessSubTypes = createSelector(
  [
    state => state.order.create.businessSubTypes || {},
    state => state.form.newOrderForm?.values,
    getIndicators,
  ],
  (businessSubTypes, values) => values?.indicator && businessSubTypes[values?.indicator],
  (businessSubTypes, values, indicators) => {
    if (values?.indicator) {
      if (values?.splitInvoice) {
        const spIndicator = indicators.find(i =>
          i.id === values?.indicator,
        )?.splitInvoiceIndicator?.id;
        return spIndicator ? businessSubTypes[spIndicator] : [];
      }
      return businessSubTypes[values?.indicator];
    }
    return [];
  },
);

export const getIndicatorBusinessSubTypes = createSelector(
  // eslint-disable-next-line max-len
  [state => state.order.create.businessSubTypes || {}, state => state.form.newOrderForm?.values, getSplitInvoiceIndicator],
  (businessSubTypes, values, indicator) => {
    if (values?.indicatorType) {
      if (values?.splitInvoice) {
        const spIndicator = indicator?.indicator?.id;
        return spIndicator ? businessSubTypes[spIndicator] : [];
      }
      return businessSubTypes[values?.indicatorType];
    }
    return [];
  },
);


export const getSplitInvoice = createSelector(
  [getIndicators, state => state.form.newOrderForm && state.form.newOrderForm.values],
  (types, values) => types?.find(t => t.id === values?.indicator)?.splitInvoiceIndicator,
);

export const getPaidByValues = createSelector(
  [getCostPaidBys, state => state.form.newOrderForm && state.form.newOrderForm.values],
  (costPaidBys, values) => {
    if (values && values.paidBy && costPaidBys.length > 0) {
      const selectedPaidBy = costPaidBys?.find(cpb => cpb.id === values.paidBy);
      const paidByValues = selectedPaidBy.paidByValues || [];
      return {
        key: 'paidByValue',
        label: camelCaseToFriendlyText(selectedPaidBy.paidByValueKey),
        items: paidByValues?.map(d => ({ id: d.id, text: d.description, default: d.default })),
      };
    }
    return { items: [] };
  },
);

export const getPaidByWizardInputs = createSelector(
  [getCostPaidBys, state => state.form.newOrderForm && state.form.newOrderForm.values],
  (costPaidBys, values) => {
    if (values && values.paidBy && costPaidBys.length > 0) {
      const selectedPaidBy = costPaidBys?.find(cpb => cpb.id === values.paidBy);
      const wizardInputs = selectedPaidBy.wizardInputs || [];
      return {
        key: 'wizardInput',
        label: camelCaseToFriendlyText(selectedPaidBy.paidByValueKey),
        items: wizardInputs?.filter(input => input.indicators.includes(values.indicator)),
      };
    }
    return { items: [] };
  },
);

export const getDynamicValue = createSelector(
  [
    state => state.order.create.types || [],
    state => state.form.newOrderForm && state.form.newOrderForm.values,
  ],
  (orderTypes, values) => {
    if (values && values.orderType) {
      const orderType = orderTypes?.find(ot => ot.type === values.orderType);
      return {
        valueKey: orderType.valueKey,
        key: 'value',
        label: camelCaseToFriendlyText(orderType.valueKey),
        items: orderType.values?.map(d => ({
          id: d.code,
          text: d.description,
        })),
      };
    }
    return { items: [] };
  },
);

export const getScheduledItems = state =>
  state.order.create.scheduledItems?.map(si => ({
    ...si,
    key: si.id,
  }));

export const hasValues = createSelector(
  [
    state => state.order.create.types || [],
    state => state.form.newOrderForm && state.form.newOrderForm.values,
  ],
  (orderTypes, values) => {
    if (values && values.orderType && orderTypes.length > 0) {
      return orderTypes?.find(ot => ot.type === values.orderType).values.length > 0;
    }
    return false;
  },
);

export const getActionOrderDescription = state =>
  state.smartAction.actionForCreatingOrder.description;
